import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Wizard, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import {
  CompetenciesCompetenciesListDocument,
  CompetenciesCreateCompetencyDocument,
  CompetenciesDataCollectionDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { type FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { validateLevels } from 'modules/competencies/components/CompetencyLevelsForm'
import { useCompetenciesFeatures } from 'modules/competencies/hooks/useCompetenciesFeatures'

import { AssignationStep } from './AssignationStep'
import { AudienceStep } from './AudienceStep'
import { BasicInformationStep } from './BasicInformationStep'
import { LevelsStep } from './LevelsStep'
import { type WizardData } from './types'

interface Props {
  canManageJobCatalogTree: boolean
  onClose: () => void
}

export const NewCompetencyWizard: React.FC<Props> = ({ canManageJobCatalogTree, onClose }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { isJobCatalogTreeEnabled } = useCompetenciesFeatures()

  const [create, { loading: creating }] = useMutation(CompetenciesCreateCompetencyDocument, {
    extractorFn: (data) => data.competenciesMutations.createCompetenciesCompetency,
  })

  const [resetSteps, setResetSteps] = useState(false)

  const handleCreateCompetency = async (
    values: WizardData,
    setErrors: (errors: FormErrors<WizardData>) => void
  ) => {
    const everyone = values.audience === 'all'

    const levels = values.levels.map((v, index) => ({
      name: v.name,
      description: v.description,
      jobCatalogTreeNodeUuids: v.jobCatalogTreeNodeUuids,
      position: index,
    }))

    const result = await create({
      variables: {
        competencyGroupId: values.competencyGroupId,
        description: values.description,
        everyone,
        levels: levels.length > 0 ? levels : undefined,
        name: values.name,
        roleIds: everyone ? [] : compact(values.roleIds),
        jobCatalogTreeNodeUuids: values.jobCatalogTreeNodeUuids,
      },
      refetchQueries: [CompetenciesCompetenciesListDocument, CompetenciesDataCollectionDocument],
    })

    const handled = handleResult(result, (errors) => {
      setErrors(errors)
      setResetSteps(true)
    })

    return handled
  }

  const form = useForm<WizardData>({
    defaultValues: {
      name: '',
      description: '',
      competencyGroupId: undefined,
      audience: isJobCatalogTreeEnabled ? 'jobs' : 'all',
      roleIds: [],
      levels: [],
    },
    validate: (values) => {
      const errors: FormErrors<WizardData> = {}

      const { errors: levelErrors, hasError } = validateLevels(values.levels)
      if (hasError) {
        errors.levels = Object.values(levelErrors)
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      if (creating) return Promise.reject()

      const result = await handleCreateCompetency(values, setErrors)

      if (result.success) {
        const competencyId = result.data?.competency?.id

        track(trackingEvents.competencies.createCompetency, {
          has_group: Boolean(values.competencyGroupId),
        })

        app.setFlash({
          type: 'correct',
          message: i18n.t('competencies_v3.new_competency_wizard.created_successfully'),
        })

        if (competencyId && isJobCatalogTreeEnabled) {
          navigate(
            navigation.competencies.competencyId.fullPath({
              competencyId,
            })
          )
        }

        onClose()
      }

      return result
    },
  })

  const formHandleSubmit = form.handleSubmit
  const handleSubmit = useUnsafeCallback(() => {
    formHandleSubmit()
  }, [formHandleSubmit])

  const steps = useUnsafeMemo(() => {
    return compact([
      {
        title: i18n.t('competencies_v3.new_competency_wizard.basic_information_step.title'),
        nextText: i18n.t('competencies_v3.new_competency_wizard.next'),
        render: (props: WizardStepRenderParams<WizardData>) => (
          <BasicInformationStep {...props} errors={form.errors} />
        ),
        isCompleted: (data: WizardData) => !!data.name && !creating,
      },
      !isJobCatalogTreeEnabled && {
        title: i18n.t('competencies_v3.new_competency_wizard.audience_step.title'),
        nextText: i18n.t('competencies_v3.new_competency_wizard.create'),
        render: (props: WizardStepRenderParams<WizardData>) => <AudienceStep {...props} />,
        isCompleted: (data: WizardData) => !!data.audience && !creating,
      },
      isJobCatalogTreeEnabled && {
        title: i18n.t('competencies_v3.levels.title'),
        nextText: canManageJobCatalogTree
          ? i18n.t('competencies_v3.new_competency_wizard.next')
          : i18n.t('common.save'),
        render: (props: WizardStepRenderParams<WizardData>) => (
          <LevelsStep {...props} errors={form.errors} />
        ),
        isCompleted: (_data: WizardData) => !creating,
      },
      isJobCatalogTreeEnabled &&
        canManageJobCatalogTree && {
          title: i18n.t('competencies_v3.new_competency_wizard.assignation_step.title'),
          nextText: i18n.t('common.save'),
          render: (props: WizardStepRenderParams<WizardData>) => <AssignationStep {...props} />,
          isCompleted: (_data: WizardData) => !creating,
        },
    ])
  }, [isJobCatalogTreeEnabled, canManageJobCatalogTree, form.errors, creating])

  // There's no mechanism in the Wizard to move back the user to a specific step when it happens an error,
  // it's only possible to reset the steps by passing a prop. We need to set the resetSteps state as false
  // whenever it changes to true to allow the user proceed. If we keep it as true, the wizard
  // blocks the user from proceeding to the next step
  useEffect(() => {
    if (resetSteps) setResetSteps(false)
  }, [resetSteps])

  return (
    <Wizard.Modal
      initialData={form.initialValues}
      size="large"
      icon="window-user"
      title={i18n.t('competencies_v3.new_competency_wizard.title')}
      onClose={onClose}
      onSubmit={handleSubmit}
      onDataChanged={form.setValues}
      steps={steps}
      resetSteps={resetSteps}
    />
  )
}
